import React from "react"

import { motion } from "framer-motion"
import { buildMutation, compress, useMutation } from "micro-graphql-react"

import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/solid"

import EditableDisplay from "components/Forms/EditableDisplay"
import RemoveButton from "components/RemoveButton"

import LessonResource from "./LessonResource"
import NewResource from "./NewResource"
import lessonTopicsFragment from "./lessonTopicsFragment"

const LessonTopic = ({ topic, first, last }) => {
  const { runMutation } = useMutation(buildMutation(updateLessonTopicMutation))
  const { runMutation: incrementOrder, running: incrementOrderRunning } =
    useMutation(buildMutation(incrementOrderMutation))
  const { runMutation: decrementOrder, running: decrementOrderRunning } =
    useMutation(buildMutation(decrementOrderMutation))

  const TopicName = ({ children }) => {
    const { runMutation } = useMutation(
      buildMutation(removeLessonTopicMutation)
    )

    return (
      <div className="flex items-center">
        <RemoveButton onClick={() => runMutation({ id: topic.id })} />
        <h4 className="d-inline-block m-0">{children}</h4>
      </div>
    )
  }

  return (
    <motion.li
      positionTransition
      className="rounded-lg border border-solid border-gray-300 bg-neutral-50 p-3 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <EditableDisplay
          displayValue={topic.name}
          save={({ value }) => runMutation({ id: topic.id, name: value })}
          tag={TopicName}
          height="auto"
        />
        <div className="flex items-center">
          <button
            className="mx-2 cursor-pointer rounded-md border-none bg-inherit pt-2 text-xl hover:bg-gray-200"
            onClick={() => incrementOrder({ id: topic.id })}
            disabled={last || incrementOrderRunning}
          >
            <ArrowDownIcon className="h-6 w-6" />
          </button>
          <button
            className="mx-2 cursor-pointer rounded-md border-none bg-inherit pt-2 text-xl hover:bg-gray-200"
            onClick={() => decrementOrder({ id: topic.id })}
            disabled={first || decrementOrderRunning}
          >
            <ArrowUpIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div className="mt-3 rounded-md border border-solid border-gray-200 bg-white p-3">
        <span className="text-lg font-semibold">Worksheets</span>
        <ul className="mt-2 list-none p-0">
          {topic.lessonResources.map(resource => (
            <LessonResource
              resource={resource}
              key={resource.id}
              first={resource === topic.lessonResources[0]}
              last={
                resource ===
                topic.lessonResources[topic.lessonResources.length - 1]
              }
            />
          ))}
        </ul>
        <div className="flex items-center">
          <NewResource lessonTopicId={topic.id} signedUrl={topic.signedUrl} />
        </div>
      </div>
    </motion.li>
  )
}

const updateLessonTopicMutation = compress`
  mutation($id: ID!, $name: String!) {
    updateLessonTopic(id: $id, name: $name) {
      lessonTopic {
        id
        name
      }
    }
  }
`

const decrementOrderMutation = compress`
  ${lessonTopicsFragment}
  mutation($id: ID!) {
    decrementLessonTopicOrder(id: $id) {
      lessonPlan {
        lessonTopics {
          ...LessonTopicsFields
        }
      }
    }
  }
`

const incrementOrderMutation = compress`
  ${lessonTopicsFragment}
  mutation($id: ID!) {
    incrementLessonTopicOrder(id: $id) {
      lessonPlan {
        lessonTopics {
          ...LessonTopicsFields
        }
      }
    }
  }
`

const removeLessonTopicMutation = compress`
  ${lessonTopicsFragment}
  mutation($id: ID!) {
    removeLessonTopic(id: $id) {
      lessonPlan {
        lessonTopics {
          ...LessonTopicsFields
        }
      }
    }
  }
`

export default LessonTopic
